// The hider deck — the cards a hider draws (by answering seeker questions),
// holds in hand, and plays. Card faces live in /public/deck/<id>.jpg.
//
// Default copy-counts come from `other card info.md`; the GM can adjust them and
// toggle the optional Move card at setup. Time-bonus values scale with game size
// (S/M/L), reusing the curse GameSize.
import { curseById, curseImage, resolveText, type GameSize } from "./curses";

export type DeckCardKind =
  | "time"            // time-bonus card; its value is auto-summed into the hide score
  | "duplicate"       // copy another card in hand
  | "randomize"       // play instead of answering: a random same-category question is answered
  | "veto"            // play instead of answering: no answer, no reward
  | "draw1expand1"    // draw 1; +1 hand size for the round
  | "discard1draw2"   // discard 1, draw+keep 2
  | "discard2draw3"   // discard 2, draw+keep 3
  | "move";           // discard hand, relocate; timed frozen window; not in endgame

export interface DeckCard {
  id: string;          // slug; image is /deck/<id>.jpg
  kind: DeckCardKind;
  name: string;
  description: string; // may contain `{n}` (size value)
  count: number;       // default copies in the deck
  optional?: boolean;  // GM toggles inclusion (Move)
  isPowerup?: boolean; // a playable action card (not a time bonus)
  bonus?: { S: number; M: number; L: number }; // time cards only
  size?: { S: string; M: string; L: string };  // for `{n}` (Move)
}

export const DECK_CARDS: DeckCard[] = [
  // ---- time bonuses (value auto-summed; best-N claimed in pods) ----
  { id: "time-xl", kind: "time", name: "Time Bonus", description: "Adds {n} minutes to your hide time.", count: 2, bonus: { S: 12, M: 18, L: 30 }, size: { S: "12", M: "18", L: "30" } },
  { id: "time-large", kind: "time", name: "Time Bonus", description: "Adds {n} minutes to your hide time.", count: 2, bonus: { S: 8, M: 12, L: 20 }, size: { S: "8", M: "12", L: "20" } },
  { id: "time-med", kind: "time", name: "Time Bonus", description: "Adds {n} minutes to your hide time.", count: 9, bonus: { S: 6, M: 9, L: 15 }, size: { S: "6", M: "9", L: "15" } },
  { id: "time-medsmall", kind: "time", name: "Time Bonus", description: "Adds {n} minutes to your hide time.", count: 13, bonus: { S: 4, M: 6, L: 10 }, size: { S: "4", M: "6", L: "10" } },
  { id: "time-small", kind: "time", name: "Time Bonus", description: "Adds {n} minutes to your hide time.", count: 23, bonus: { S: 2, M: 3, L: 5 }, size: { S: "2", M: "3", L: "5" } },
  // ---- powerups ----
  { id: "duplicate", kind: "duplicate", name: "Duplicate Another Card", isPowerup: true, count: 2, description: "Play as a copy of any other card in your hand. May be used to duplicate a time bonus at the end of your round." },
  { id: "randomize", kind: "randomize", name: "Randomize Question", isPowerup: true, count: 4, description: "Play instead of answering a question. A new unasked question from the same category is chosen at random, which you answer instead." },
  { id: "veto", kind: "veto", name: "Veto Question", isPowerup: true, count: 3, description: "Play instead of answering a question. No answer is given, and no reward is earned." },
  { id: "draw1-expand1", kind: "draw1expand1", name: "Draw 1, Expand 1", isPowerup: true, count: 2, description: "Draw one card from the hider deck. For the rest of the round, you can hold one additional card in your hand." },
  { id: "discard1-draw2", kind: "discard1draw2", name: "Discard 1, Draw 2", isPowerup: true, count: 4, description: "Discard one other card from your hand. Then draw and keep two cards from the hider deck." },
  { id: "discard2-draw3", kind: "discard2draw3", name: "Discard 2, Draw 3", isPowerup: true, count: 4, description: "Discard two other cards from your hand. Then draw and keep three cards from the hider deck." },
  { id: "move", kind: "move", name: "Move", isPowerup: true, optional: true, count: 1, size: { S: "10", M: "20", L: "60" }, description: "Discard your hand and send the hiders your transit station. Grants a {n}-minute window to set a new hiding zone elsewhere; seekers are frozen and your timer pauses until it ends. Not playable in the endgame." },
];

export const DECK_BY_ID: Record<string, DeckCard> = Object.fromEntries(DECK_CARDS.map((c) => [c.id, c]));
export const deckCardById = (id: string): DeckCard | null => DECK_BY_ID[id] ?? null;
export const deckImage = (id: string): string => `/deck/${id}.jpg`;
export const isCurseCard = (id: string): boolean => !!curseById(id);

// Unified display info for any card in the hand — a deck card OR a curse card.
export interface CardFace { id: string; name: string; image: string; description: string; isCurse: boolean; isPowerup: boolean; kind?: DeckCardKind }
export function cardFace(id: string, size: GameSize): CardFace | null {
  const d = DECK_BY_ID[id];
  if (d) return { id, name: d.name, image: deckImage(id), description: deckText(d, size), isCurse: false, isPowerup: !!d.isPowerup, kind: d.kind };
  const cu = curseById(id);
  if (cu) return { id, name: cu.name, image: curseImage(id), description: resolveText(cu.description, cu, size), isCurse: true, isPowerup: true };
  return null;
}

// Default deck composition (cardId -> copies), GM-overridable. `includeMove` off
// drops the optional Move card.
export function defaultDeckCounts(includeMove = true): Record<string, number> {
  const out: Record<string, number> = {};
  for (const c of DECK_CARDS) { if (c.optional && !includeMove) continue; out[c.id] = c.count; }
  return out;
}
export const DEFAULT_HAND_SIZE = 5;
export const DEFAULT_MAX_TIME_CARDS = 5; // pods: best-N time bonuses claimed at round end

// Substitute `{n}` with the size value.
export function deckText(card: DeckCard | null | undefined, size: GameSize): string {
  if (!card) return "";
  const v = card.size ? card.size[size] : "";
  return card.description.replace(/\{n\}/g, v);
}
// A time card's minutes for the current size (0 for non-time cards).
export function timeValue(card: DeckCard | null | undefined, size: GameSize): number {
  return card?.bonus ? card.bonus[size] : 0;
}
// Sum the best `maxCards` time bonuses in a list of card ids (pods cap the count).
export function bestTimeBonus(cardIds: string[], size: GameSize, maxCards: number | null): number {
  const vals = cardIds.map((id) => timeValue(deckCardById(id), size)).filter((v) => v > 0).sort((a, b) => b - a);
  const take = maxCards == null ? vals.length : Math.min(maxCards, vals.length);
  return vals.slice(0, take).reduce((s, v) => s + v, 0);
}
